/**
 * Auth, tenancy and settings API — shared by web and mobile.
 */
import type { AslenClient, Json, Tables, TablesInsert } from '../db';
import type { MemberRole } from '../constants';
import { AppError, toAppError, unwrap } from './errors';

// ── auth ─────────────────────────────────────────────────────────────────────
export async function signInWithPassword(client: AslenClient, email: string, password: string) {
  const { data, error } = await client.auth.signInWithPassword({ email: email.trim().toLowerCase(), password });
  if (error) throw toAppError(error);
  return data;
}

export async function sendMagicLink(client: AslenClient, email: string, redirectTo: string) {
  // shouldCreateUser: false → only invited / existing users can sign in (signup is disabled)
  const { error } = await client.auth.signInWithOtp({
    email: email.trim().toLowerCase(),
    options: { shouldCreateUser: false, emailRedirectTo: redirectTo },
  });
  if (error) throw toAppError(error);
}

export async function sendPasswordReset(client: AslenClient, email: string, redirectTo: string) {
  const { error } = await client.auth.resetPasswordForEmail(email.trim().toLowerCase(), { redirectTo });
  if (error) throw toAppError(error);
}

export async function updatePassword(client: AslenClient, password: string) {
  const { error } = await client.auth.updateUser({ password });
  if (error) throw toAppError(error);
}

export async function signOut(client: AslenClient) {
  const { error } = await client.auth.signOut();
  if (error) throw toAppError(error);
}

// ── invitations ──────────────────────────────────────────────────────────────
export type Invitation = {
  invitation_id: string;
  company_id: string;
  company_name: string;
  company_brand: string | null;
  email: string;
  role: MemberRole;
  display_name: string | null;
  inviter_name: string | null;
  inviter_initials: string | null;
  inviter_color: string | null;
  expires_at: string;
  status: 'pending' | 'accepted' | 'declined' | 'revoked' | 'expired';
};

export async function getInvitation(client: AslenClient, token: string): Promise<Invitation | null> {
  const rows = unwrap(await client.rpc('get_invitation', { p_token: token })) as unknown as Invitation[];
  return rows[0] ?? null;
}

export async function myPendingInvitations(client: AslenClient) {
  return unwrap(await client.rpc('my_pending_invitations'));
}

export async function acceptInvitation(client: AslenClient, token: string, displayName?: string) {
  return unwrap(await client.rpc('accept_invitation', { p_token: token, p_display_name: displayName })) as string;
}

export async function declineInvitation(client: AslenClient, token: string) {
  const { error } = await client.rpc('decline_invitation', { p_token: token });
  if (error) throw toAppError(error);
}

export async function inviteMember(
  client: AslenClient,
  companyId: string,
  input: { email: string; role: MemberRole; displayName?: string },
  inviteUrlBase?: string,
) {
  const inv = unwrap(
    await client.rpc('invite_member', {
      p_company: companyId,
      p_email: input.email,
      p_role: input.role,
      p_display_name: input.displayName,
    }),
  ) as unknown as Tables<'company_invitations'>;
  // Best effort: the invite-member Edge Function sends the e-mail with the service role.
  let emailed = false;
  try {
    const { error } = await client.functions.invoke('invite-member', {
      body: { invitation_id: inv.id, redirect_to: inviteUrlBase ? `${inviteUrlBase}?token=${inv.token}` : undefined },
    });
    emailed = !error;
  } catch {
    emailed = false;
  }
  return { invitation: inv, emailed, link: inviteUrlBase ? `${inviteUrlBase}?token=${inv.token}` : null };
}

// ── companies & membership ───────────────────────────────────────────────────
export type Membership = Tables<'company_members'> & { company: Tables<'companies'> };

export async function listMemberships(client: AslenClient): Promise<Membership[]> {
  const { data: user } = await client.auth.getUser();
  if (!user.user) return [];
  const rows = unwrap(
    await client
      .from('company_members')
      .select('*, company:companies(*)')
      .eq('user_id', user.user.id)
      .is('deleted_at', null)
      .order('created_at'),
  );
  return rows as unknown as Membership[];
}

export async function createCompany(
  client: AslenClient,
  input: { name: string; displayName: string; legalName?: string; brand?: string; taxNo?: string; taxOffice?: string; reportingCurrency?: string },
) {
  return unwrap(
    await client.rpc('create_company', {
      p_name: input.name,
      p_display_name: input.displayName,
      p_legal_name: input.legalName,
      p_brand: input.brand,
      p_tax_no: input.taxNo,
      p_tax_office: input.taxOffice,
      p_reporting_currency: input.reportingCurrency ?? 'TRY',
    }),
  ) as string;
}

export async function updateCompany(client: AslenClient, companyId: string, patch: Partial<Tables<'companies'>>) {
  return unwrap(await client.from('companies').update(patch).eq('id', companyId).select().single());
}

export type MemberRow = {
  member_id: string;
  user_id: string;
  email: string;
  display_name: string;
  initials: string | null;
  color: string | null;
  role: MemberRole;
  last_sign_in_at: string | null;
  joined_at: string;
};

export async function listMembers(client: AslenClient, companyId: string): Promise<MemberRow[]> {
  return unwrap(await client.rpc('list_company_members', { p_company: companyId })) as unknown as MemberRow[];
}

export async function listPendingInvitations(client: AslenClient, companyId: string) {
  return unwrap(
    await client
      .from('company_invitations')
      .select('*')
      .eq('company_id', companyId)
      .is('accepted_at', null)
      .is('declined_at', null)
      .is('revoked_at', null)
      .gt('expires_at', new Date().toISOString())
      .order('created_at', { ascending: false }),
  );
}

export async function revokeInvitation(client: AslenClient, id: string) {
  unwrap(await client.from('company_invitations').update({ revoked_at: new Date().toISOString() }).eq('id', id).select());
}

export async function updateMemberRole(client: AslenClient, memberId: string, role: MemberRole) {
  return unwrap(await client.from('company_members').update({ role }).eq('id', memberId).select().single());
}

export async function updateMemberName(client: AslenClient, memberId: string, displayName: string) {
  return unwrap(await client.from('company_members').update({ display_name: displayName }).eq('id', memberId).select().single());
}

// ── settings ─────────────────────────────────────────────────────────────────
export type SettingsMap = Record<string, Record<string, Json | undefined>>;

export async function getSettings(client: AslenClient, companyId: string): Promise<SettingsMap> {
  const rows = unwrap(await client.from('settings').select('key, value').eq('company_id', companyId));
  return Object.fromEntries(rows.map((r) => [r.key, (r.value ?? {}) as Record<string, Json | undefined>]));
}

export async function setSetting(client: AslenClient, companyId: string, key: string, value: Json) {
  unwrap(await client.from('settings').upsert({ company_id: companyId, key, value }, { onConflict: 'company_id,key' }).select());
}

// ── reference data ───────────────────────────────────────────────────────────
export async function listCurrencies(client: AslenClient) {
  return unwrap(await client.from('currencies').select('*').order('code'));
}

export async function listMarketplaces(client: AslenClient) {
  return unwrap(await client.from('marketplaces').select('*').order('sort'));
}

export async function listUnits(client: AslenClient, companyId: string) {
  return unwrap(await client.from('units').select('*').eq('company_id', companyId).order('sort'));
}

export async function updateUnit(client: AslenClient, id: string, patch: Partial<Tables<'units'>>) {
  return unwrap(await client.from('units').update(patch).eq('id', id).select().single());
}

export async function listTaxRates(client: AslenClient, companyId: string) {
  return unwrap(await client.from('tax_rates').select('*').eq('company_id', companyId).order('sort'));
}

export async function upsertTaxRate(client: AslenClient, row: TablesInsert<'tax_rates'>) {
  return unwrap(await client.from('tax_rates').upsert(row).select().single());
}

export async function listNumberSequences(client: AslenClient, companyId: string) {
  return unwrap(
    await client.from('number_sequences').select('*').eq('company_id', companyId).order('doc_type').order('year', { ascending: false }),
  );
}

/**
 * The locations a user asked for: live, active, and never the warehouse's default location.
 * From Phase 2 on every warehouse owns one auto-created default location (`is_default`, code = the
 * warehouse code) — stock plumbing, not something anyone typed into the wizard, so it must not be
 * counted as one of the warehouse's N locations. The flag is absent before those migrations are
 * pushed, and a row without it is simply a normal location.
 */
export function userLocations<T extends { code: string; is_active?: boolean | null; is_default?: boolean | null; deleted_at?: string | null }>(
  locations: T[] | null | undefined,
): T[] {
  return (locations ?? []).filter((l) => !l.is_default && l.is_active !== false && !l.deleted_at).sort((a, b) => a.code.localeCompare(b.code));
}

export async function listWarehouses(client: AslenClient, companyId: string) {
  const rows = unwrap(
    await client
      .from('warehouses')
      // locations(*) and not a column list: is_default only exists once the Phase 2 migrations are
      // pushed, and naming a missing column would fail the whole query on the live schema.
      .select('*, locations(*)')
      .eq('company_id', companyId)
      .is('deleted_at', null)
      .order('type')
      .order('sort'),
  );
  // callers count `locations` ("3 lokasyon", and the count the setup wizard re-saves)
  return rows.map((w) => ({ ...w, locations: userLocations(w.locations) }));
}

/** Creates/updates own warehouses (with N generated locations) and FBA warehouses per marketplace. */
export async function saveWarehouseSetup(
  client: AslenClient,
  companyId: string,
  own: { id?: string; code: string; name: string; locations: number }[],
  marketplaces: { id: string; name: string; country: string }[],
) {
  for (const [i, w] of own.entries()) {
    const saved = unwrap(
      await client
        .from('warehouses')
        .upsert({ id: w.id, company_id: companyId, code: w.code.toUpperCase(), name: w.name, type: 'own', sort: i }, { onConflict: 'id' })
        .select()
        .single(),
    );
    // select('*') for the same reason as listWarehouses: is_default is not on the live schema yet
    const live = unwrap(await client.from('locations').select('*').eq('warehouse_id', saved.id).is('deleted_at', null));
    const taken = new Set(live.map((l) => l.code)); // every live code, the default location included — never re-create one
    const have = userLocations(live).length; // …but only the user's own locations count towards w.locations
    // From Phase 2 on, every warehouse gets a default zone "GEN" and locations must sit in one.
    // The table does not exist until those migrations are applied, so its absence is not an error here.
    const zonesQuery = await client.from('warehouse_zones').select('id, code').eq('warehouse_id', saved.id).is('deleted_at', null).order('sort');
    const zoneId = zonesQuery.error ? undefined : ((zonesQuery.data ?? []).find((z) => z.code === 'GEN') ?? (zonesQuery.data ?? [])[0])?.id;
    const toCreate: TablesInsert<'locations'>[] = [];
    const want = Math.max(0, Math.trunc(w.locations));
    for (let n = 1; have + toCreate.length < want; n++) {
      const code = `${saved.code}-${String(n).padStart(2, '0')}`;
      // zone_id is required by the Phase 2 schema and absent from the earlier one, hence the cast
      if (!taken.has(code)) toCreate.push({ company_id: companyId, warehouse_id: saved.id, code, ...(zoneId ? { zone_id: zoneId } : {}) } as TablesInsert<'locations'>);
    }
    if (toCreate.length) unwrap(await client.from('locations').insert(toCreate).select('id'));
  }
  const fba = unwrap(await client.from('warehouses').select('id, marketplace_id').eq('company_id', companyId).eq('type', 'fba').is('deleted_at', null));
  const haveFba = new Set(fba.map((w) => w.marketplace_id));
  const wanted = new Set(marketplaces.map((m) => m.id));
  const create = marketplaces
    .filter((m) => !haveFba.has(m.id))
    .map((m, i) => ({
      company_id: companyId,
      code: `FBA-${m.id}`,
      name: `${m.name} deposu`,
      type: 'fba' as const,
      country: m.country,
      marketplace_id: m.id,
      sort: 100 + i,
    }));
  if (create.length) unwrap(await client.from('warehouses').insert(create).select('id'));
  const remove = fba.filter((w) => w.marketplace_id && !wanted.has(w.marketplace_id)).map((w) => w.id);
  if (remove.length) unwrap(await client.from('warehouses').update({ deleted_at: new Date().toISOString() }).in('id', remove).select('id'));
}

export async function removeWarehouse(client: AslenClient, id: string) {
  unwrap(await client.from('warehouses').update({ deleted_at: new Date().toISOString() }).eq('id', id).select('id'));
}

// ── infra & audit ────────────────────────────────────────────────────────────
export interface InfraBudget {
  measured_at: string;
  db_bytes: number;
  storage_bytes: number;
  tables: { table: string; rows: number }[];
  limits: {
    db_limit_mb?: number;
    db_warn_mb?: number;
    storage_limit_mb?: number;
    storage_warn_mb?: number;
    actions_limit_min?: number;
    actions_budget_min?: number;
    actions_used_min?: number;
    actions_measured_at?: string;
    last_backup_at?: string;
    last_backup_bytes?: number;
    last_backup_status?: string;
  };
}

export async function getInfraBudget(client: AslenClient, companyId: string): Promise<InfraBudget> {
  return unwrap(await client.rpc('get_infra_budget', { p_company: companyId })) as unknown as InfraBudget;
}

export async function listAuditLog(client: AslenClient, companyId: string, limit = 100) {
  return unwrap(
    await client
      .from('audit_log')
      .select('id, table_name, row_id, action, actor_id, at, before, after')
      .eq('company_id', companyId)
      .order('at', { ascending: false })
      .limit(limit),
  );
}

// ── notifications ────────────────────────────────────────────────────────────
export async function listNotifications(client: AslenClient, companyId: string, limit = 50) {
  return unwrap(
    await client
      .from('notifications')
      .select('id, type, title, body, ref_type, ref_id, read_at, created_at')
      .eq('company_id', companyId)
      .order('created_at', { ascending: false })
      .limit(limit),
  );
}

export async function markNotificationsRead(client: AslenClient, ids: string[]) {
  if (!ids.length) return;
  unwrap(await client.from('notifications').update({ read_at: new Date().toISOString() }).in('id', ids).is('read_at', null));
}

export async function listNotificationRules(client: AslenClient, companyId: string) {
  return unwrap(await client.from('notification_rules').select('*').eq('company_id', companyId).order('type'));
}

export async function updateNotificationRule(client: AslenClient, id: string, patch: { is_active?: boolean; params?: Json }) {
  unwrap(await client.from('notification_rules').update(patch).eq('id', id).select('id'));
}

export function assertCompany(companyId: string | null | undefined): asserts companyId is string {
  if (!companyId) throw new AppError('Şirket seçili değil', 'rule');
}
